import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * El diálogo de la aplicación. Su ancho sale del token `--container-dialogo` y **no es decisión de
 * quien lo abre**: la clase es la misma para todos, así que un diálogo nuevo nace ancho.
 */
export function Modal({
  abierto,
  titulo,
  onCerrar,
  children,
}: {
  abierto: boolean;
  titulo: string;
  onCerrar: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  // El foco entra una vez, al abrir, y en su propio efecto: junto al listener de Escape se
  // reenfoca en cada render, porque `onCerrar` cambia de identidad si el padre lo declara en línea.
  useEffect(() => {
    if (!abierto) return;
    panel.current?.querySelector<HTMLElement>('button, input, select')?.focus();
  }, [abierto]);

  // En captura y marcado: el Esc que cierra el diálogo llega atendido a los atajos de la pantalla, que
  // lo ignoran por `defaultPrevented`.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onCerrar();
    };
    window.addEventListener('keydown', alTeclear, true);
    return () => window.removeEventListener('keydown', alTeclear, true);
  }, [abierto, onCerrar]);

  if (!abierto) return null;
  return (
    <div className="overlay fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCerrar} role="dialog" aria-modal="true">
      <div
        ref={panel}
        className="rounded-sheet bg-card border border-border max-h-[90dvh] w-full max-w-dialogo overflow-y-auto p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-3 text-titulo font-extrabold">{titulo}</h2>
        {children}
      </div>
    </div>
  );
}
